import 'regenerator-runtime/runtime';
import React from 'react';
import ReactDOM from 'react-dom';
import './index.scss';
import * as immer from 'immer';
import * as zustand from 'zustand';
import axios from 'axios';
import lodash from 'lodash';
import * as useImmer from 'use-immer';
import * as yup from 'yup';
import { Formik } from 'formik';
import type { Breakpoints } from './lib';
import { intl, intlFmt } from './lib';
import FocusTrap from 'focus-trap-react';

// export third-party libraries for use in the portlets.
// don't forget to keep the 'externals' property in `webpack.config.base.js` in sync!
export { React };
export { ReactDOM };
export { axios };
export { immer };
export { lodash };
export { useImmer };
export { yup };
export { Formik };
export { zustand };
export { FocusTrap };

// export our common components and library code
export * from './lib';
export * from './components';
export * from './util';
export * from './saf';

export interface SenseConstantsType {
  /**
   * WRPH build version.
   */
  version: string;
  /**
   * User language (e.g en).
   */
  language: string;
  /**
   * User country (e.g. US).
   */
  country: string;
  /**
   * User locale (e.g. en-US).
   */
  locale: string;
  /**
   * Whether the page is set to debugging mode.
   */
  isDebug: boolean;
  /**
   * Whether we are in Embedded Portal.
   */
  isEmbeddedTheme: boolean;
  /**
   * url to the pdf.js lib (probably not needed!).
   */
  pdfJsUrl: string;
  /**
   * The name of the page (e.g. health-record).
   */
  page: string;
  /**
   * The name of the organization (e.g. physician-portal).
   */
  organization: string;

  /**
   * The logout URL to consider.
   */
  logoutUrl: string;

  /**
   * The color settings.
   */
  colorSettings: string;

  /**
   * Flag that indicates if the current request is from mobile app.
   */
  isMobileAppRequest: boolean;
}

/**
 * Adds headerManager function into window. Export 2 functions that can manipulate application's header.
 * The Css definition that go hand in hand can be found in EHealthTheme/src-web/css/main.scss
 *
 * @returns {{removeHeader: removeHeader, restoreHeader: restoreHeader}}
 */
declare function headerManager(): {
  /**
   * Function adds 'remove' into classList properties into #banner. Element with id=banner is the application header.
   * Adding the 'remove' into its classList will result in hiding the header completely. Css definition can be found in EHealthTheme/src-web/css/main.scss
   */
  removeHeader(): void;
  /**
   * Function remove 'remove' from classList properties in #banner. Element with id=banner is the application header.
   * Removing the 'remove' from its classList will result in showing the header completely. Css definition can be found in EHealthTheme/src-web/css/main.scss
   */
  restoreHeader(): void;
};

/**
 * Function that remove the application's header for window widths less than {maxPixels}
 * If the maxWidth is not specified, the header is removed regardless of the window width.
 * The Css definition that go hand in hand can be found in EHealthTheme/src-web/css/main.scss
 *
 * @param breakpoint the maxWidth where the header is removed.
 */
declare function removeHeaderMatchingBreakpoint(breakpoint?: Breakpoints): void;

declare global {
  /**
   * Global constants defined by {@file loadBasicResources.jsp}.
   *
   */
  const SenseConstants: SenseConstantsType;
  const EHSAvailableLanguages: Record<string, Record<string, string>>;
  const EHSCountries: Record<string, string>;
  const EHSLanguageMap: Record<string, string>;

  // Also make the constants available in `window` for convenience
  export interface Window {
    SenseConstants: typeof SenseConstants | undefined;
    EHSAvailableLanguages: typeof EHSAvailableLanguages | undefined;
    EHSCountries: typeof EHSCountries | undefined;
    EHSLanguageMap: typeof EHSLanguageMap | undefined;
    EHSPageLogo: string | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Liferay: any | undefined;
    headerManager: typeof headerManager;
    removeHeaderMatchingBreakpoint: typeof removeHeaderMatchingBreakpoint;
  }
}

// TODO: more translations will be needed here because the yup messages are so detailed!
// See https://github.com/jquense/yup#using-a-custom-locale-dictionary/https://github.com/jquense/yup/blob/master/src/locale.js (and autocomplete inside the objects) for usage details
yup.setLocale({
  mixed: {
    default: intl('ext.field.validationMessage'),
    required: intl('ext.field.requiredMessage'),
    oneOf: intl('react.field.oneOf'),
    notOneOf: intl('react.field.notOneOf'),
    notType: ({ type, value, originalValue }) => {
      const isCast = originalValue != null && originalValue !== value;

      if (!isCast) {
        return intlFmt('react.field.notType', type);
      }

      return intlFmt('react.field.notType.casted', type, originalValue);
    }
  },
  date: {
    max: intl('react.field.date.max'),
    min: intl('react.field.date.min')
  },
  string: {
    email: intl('react.field.string.email'),
    url: intl('react.field.string.url'),
    matches: intl('react.field.string.regexmatch'),
    min: intl('react.field.string.min'),
    max: intl('react.field.string.max'),
    length: intl('react.field.string.length')
  },
  number: {
    min: intl('react.field.number.min'),
    max: intl('react.field.number.max'),
    lessThan: intl('react.field.number.lessThan'),
    moreThan: intl('react.field.number.moreThan'),
    positive: intl('react.field.number.positive'),
    negative: intl('react.field.number.negative')
  },
  array: {
    min: intl('react.field.array.min'),
    max: intl('react.field.array.max')
  }
});
